/**
 * Animation Tokens - Workshop Recall
 * Premium, mechanical feel inspired by F1 pit-wall instrumentation
 */

// Duration tokens (ms)
export const duration = {
  // Micro transitions (buttons, chips, press states)
  micro: 140,
  microFast: 120,
  microSlow: 160,

  // Card/element transitions
  card: 200,
  cardEntrance: 220,

  // Scene transitions (page changes, modals)
  scene: 280,
  sceneExpand: 320,

  // Layout reflow (FLIP animations)
  layout: 250,

  // Showcase/ambient
  showcase: 800,
  showcaseCrossfade: 700,

  // Stagger delays
  stagger: 15, // per item
  staggerMax: 180, // cap total stagger time
} as const;

// Easing curves
export const easing = {
  // Fast out, slow in - mechanical feel
  standard: [0.4, 0.0, 0.2, 1],

  // Quick start, controlled finish
  decelerate: [0.0, 0.0, 0.2, 1],

  // Slow start, quick finish
  accelerate: [0.4, 0.0, 1, 1],

  // Snappy, precision feel
  sharp: [0.4, 0.0, 0.6, 1],

  // Smooth settle
  settle: [0.25, 0.1, 0.25, 1],
} as const;

// CSS easing strings
export const easingCSS = {
  standard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
  accelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
  sharp: 'cubic-bezier(0.4, 0.0, 0.6, 1)',
  settle: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
} as const;

// Framer Motion transition presets
export const transitions = {
  // Micro interactions (hover, press, toggle)
  micro: {
    duration: duration.micro / 1000,
    ease: easing.sharp,
  },

  // Card press/release
  press: {
    duration: duration.microFast / 1000,
    ease: easing.sharp,
  },

  // Card entrance (staggered)
  cardEntrance: {
    duration: duration.cardEntrance / 1000,
    ease: easing.decelerate,
  },

  // Layout/FLIP transitions
  layout: {
    duration: duration.layout / 1000,
    ease: easing.standard,
  },

  // Shared element (card → detail)
  sharedElement: {
    duration: duration.sceneExpand / 1000,
    ease: easing.settle,
  },

  // Overlay fade
  overlay: {
    duration: duration.micro / 1000,
    ease: easing.standard,
  },

  // Showcase crossfade
  showcase: {
    duration: duration.showcaseCrossfade / 1000,
    ease: easing.settle,
  },
} as const;

// Animation variants for Framer Motion
export const variants = {
  // Card entrance (for staggered grid)
  cardEntrance: {
    hidden: {
      opacity: 0,
      y: 10,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: transitions.cardEntrance,
    },
  },

  // Card press state
  cardPress: {
    rest: {
      scale: 1,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    pressed: {
      scale: 0.99,
      boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
      transition: transitions.press,
    },
    hover: {
      scale: 1.01,
      boxShadow: '0 8px 12px -2px rgba(0, 0, 0, 0.15)',
      transition: transitions.micro,
    },
  },

  // Fade in
  fadeIn: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: transitions.micro,
    },
  },

  // Slide up fade
  slideUp: {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: transitions.cardEntrance,
    },
  },

  // Detail panel entrance (after image settles)
  detailPanel: {
    hidden: {
      opacity: 0,
      x: 20,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        ...transitions.cardEntrance,
        delay: 0.08, // 80ms after image settles
      },
    },
  },

  // Image settle (tiny scale settle after decode)
  imageSettle: {
    loading: {
      scale: 1.01,
      opacity: 0.8,
    },
    loaded: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.12,
        ease: easing.settle,
      },
    },
  },

  // Stagger container
  staggerContainer: {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: duration.stagger / 1000,
        delayChildren: 0.02,
      },
    },
  },

  // Overlay backdrop
  backdrop: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.14 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.1 },
    },
  },
} as const;

// Shimmer animation for skeletons
export const shimmerKeyframes = `
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

// Shimmer gradient (pit-wall style - subtle, directional)
export const shimmerGradient = `
  linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.03) 20%,
    rgba(255, 255, 255, 0.06) 50%,
    rgba(255, 255, 255, 0.03) 80%,
    rgba(255, 255, 255, 0) 100%
  )
`;

// Confidence bar animation
export const confidenceBarAnimation = {
  initial: { scaleX: 0, transformOrigin: 'left' },
  animate: (value: number) => ({
    scaleX: value / 100,
    transition: {
      duration: 0.24,
      ease: easing.decelerate,
      delay: 0.1,
    },
  }),
};

// Ken Burns presets for showcase
export const kenBurns = {
  // Slow zoom in
  zoomIn: {
    initial: { scale: 1, x: 0, y: 0 },
    animate: {
      scale: 1.08,
      transition: {
        duration: 12,
        ease: 'linear',
      },
    },
  },
  // Slow pan right
  panRight: {
    initial: { scale: 1.05, x: '-2%' },
    animate: {
      x: '2%',
      transition: {
        duration: 12,
        ease: 'linear',
      },
    },
  },
  // Slow pan left
  panLeft: {
    initial: { scale: 1.05, x: '2%' },
    animate: {
      x: '-2%',
      transition: {
        duration: 12,
        ease: 'linear',
      },
    },
  },
} as const;
